import { useContext } from 'preact/hooks';
import PropTypes from 'prop-types';

import { getImageUrl, onErrorImage } from '../../../helpers/images';

import WinnerIcon from '../../shared-components/icons/WinnerIcon';

import styleSheet from '../poll.module.scss';
import { PollContext } from '../../../potg/potg.component';
import { useTranslation } from 'react-i18next';

const styles = styleSheet.locals || {};

const DEFAULT_IMAGE_WIDTH = 384; // Default width for ODIR images

/**
 * Component for a single poll option
 *
 * @param {object} props poll option props
 * @param {object} props.option poll option data
 * @param {boolean} props.showingResults poll is showing results
 * @param {object} props.transitionStyles transition styles
 * @param {string} props.transitionState transition state
 * @param {number} props.optionsVisibleDelay delay for visibility transition
 * @param {boolean} props.isSelected if option is selected by user
 * @param {boolean} props.isAnswered if option has been chosen as an answer by user
 * @param {object} props.optionTextRef option text node reference
 * @param props.optionImageWidth
 * @returns {Function} <PollOption />
 */
function PollImageOption({
    option,
    showingResults = false,
    transitionStyles,
    transitionState,
    optionsVisibleDelay,
    optionTextRef,
    optionImageWidth = DEFAULT_IMAGE_WIDTH
}) {
    const { t } = useTranslation();
    const { poll, isPreview, showVotesCounter } = useContext(PollContext);
    const editableAttr = isPreview ? { contentEditable: true } : {};
    const barTransitionStyles = {
        entered: { maxWidth: '100%' }
    };
    const winnerTransitionStyles = {
        entered: { opacity: 1, transform: 'scale(1)' }
    };

    /* eslint-disable no-magic-numbers */
    return (
        <>
            {!option.image_url && option.external_image_url && (
                <div part="potg-option-image" className={styles.pollOptionImg}>
                    <img
                        src={option.external_image_url}
                        onError={onErrorImage}
                        alt=""
                    />
                </div>
            )}
            {(option.image_url || option.image_data_preview) && (
                <div part="potg-option-image" className={styles.pollOptionImg}>
                    <img
                        src={getImageUrl(option.image_url, optionImageWidth)}
                        alt=""
                    />
                </div>
            )}

            <div className={styles.pollOptionText}>
                {showingResults && (
                    <div
                        data-cy="optionBar"
                        className={styles.pollOptionBar}
                        style={{
                            width: `${option.percentage}%`,
                            ...barTransitionStyles[transitionState],
                            transitionDelay: `${optionsVisibleDelay}ms`
                        }}
                    />
                )}

                <div className={styles.pollAnswerWrapper}>
                    <span
                        data-cy="thumbIcon"
                        className={styles.pollOptionLabel}
                        {...editableAttr}
                        ref={optionTextRef}
                    >
                        {option.text}
                    </span>

                    {showingResults && (
                        <div
                            className={styles.pollOptionData}
                            style={{
                                ...transitionStyles[transitionState],
                                transitionDelay: `${optionsVisibleDelay}ms`
                            }}
                        >
                            <div className={styles.pollOptionPercentage}>
                                {option.percentage}%
                            </div>
                            {showVotesCounter && (
                                <div className={styles.pollOptionCounter}>
                                    {option.times_selected} {t('common.votes')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {showingResults && (
                    <div
                        data-cy="winnerIcon"
                        className={styles.pollOptionRight}
                    >
                        {option.winner && poll?.state === 'closed' && (
                            <WinnerIcon
                                className={styles.pollOptionWinnerIcon}
                                style={{
                                    ...winnerTransitionStyles[transitionState]
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

PollImageOption.propTypes = {
    option: PropTypes.object.isRequired,
    showingResults: PropTypes.bool,
    transitionStyles: PropTypes.object,
    transitionState: PropTypes.string,
    optionsVisibleDelay: PropTypes.number,
    isSelected: PropTypes.bool,
    isAnswered: PropTypes.bool,
    optionTextRef: PropTypes.object,
    optionImageWidth: PropTypes.number
};

export default PollImageOption;
